import { isFunction } from 'lodash/fp';
import { navigateToWorkbench } from 'app/routes';
import { Defaults } from 'app/lib/constants';
import { DELETE_TRANSFORMATION_OPERATION } from 'app/store/__generated__/operations';
import { selectCurrentProjectId } from 'app/store/projects/selectors.js';
import { doInvalidateDownstreamDatasets } from 'app/store/datasetData/actions.js';
import { doLoadProject } from 'app/store/projects/actions.js';
import {
    SELECT_OPERATION,
    START_OPERATION,
    CANCEL_OPERATION,
    UPDATE_TRANSFORM_OPERATION,
    DELETE_TRANSFORM_OPERATION,
    CLOSE_OPERATIONS_MENU,
    OPEN_OPERATIONS_MENU,
    CREATE_TRANSFORMATION,
    CLEAR_IDS,
    ADD_OPERATION_TO_STACK
} from './types.js';
import { getCreateCommand, getUpdateCommand } from './helper.js';
import { selectOperationInputUid, selectOperationOutputUids } from './selectors.js';

export function refreshProjectData(params) {
    const { spaceSlug, viewUid } = params.payload.viewRef;
    const redirectedNodes = params.payload.redirectedNodes ?? [];
    params.dispatch(doLoadProject({ projectSlug: spaceSlug }));
    params.dispatch(doInvalidateDownstreamDatasets({ ids: [viewUid, ...redirectedNodes] }));
}

export function refreshProject(params) {
    refreshProjectData(params);
    const { spaceSlug, viewUid } = params.payload.viewRef;
    navigateToWorkbench(spaceSlug, viewUid);
}

export const doAddOperationToStack = ({ transformationType, payload }) => ({
    type: ADD_OPERATION_TO_STACK,
    api: getCreateCommand(transformationType),
    params: (state) => {
        const spaceId = selectCurrentProjectId(state);
        const viewId = selectOperationInputUid(state);

        return {
            spaceId,
            viewId,
            ...payload
        };
    },
    onSucceeded: refreshProject
});

export const doCreateTransformation = ({
    actionType = CREATE_TRANSFORMATION,
    transformationType,
    suggestedName,
    payload,
    groupUid,
    keepOpen,
    onSucceeded = Defaults.NOOP,
    onCreated
}) => ({
    type: actionType,
    api: { version: 2, name: 'CreateTransformation' },
    meta: { keepOpen },
    params: (state) => {
        const spaceUid = selectCurrentProjectId(state);
        const insertAfter = selectOperationInputUid(state);
        const insertBefore = selectOperationOutputUids(state) ?? [];
        return {
            spaceUid,
            groupUid,
            insertAfter,
            insertBefore,
            transformationType,
            suggestedName,
            payload: isFunction(payload)
                ? payload(state, { spaceUid, insertAfter, insertBefore })
                : payload
        };
    },
    onSucceeded: (params) => {
        params.dispatch({ type: CLEAR_IDS });
        onSucceeded(params);
        if (onCreated) {
            onCreated(params);
        } else {
            refreshProject(params);
        }
    }
});

export const doStartOperation = (_type, params = {}) => ({
    type: START_OPERATION,
    payload: { _type, params }
});

export const doCancelOperation = () => ({
    type: CANCEL_OPERATION
});

export const doSelectOperation = (index) => ({
    type: SELECT_OPERATION,
    payload: { index }
});

export const doUpdateTransformOperation = ({ operation, keepOpen, ...rest }) => ({
    type: UPDATE_TRANSFORM_OPERATION,
    api: getUpdateCommand(operation),
    params: rest,
    meta: { keepOpen }
});

export const doDeleteTransformOperation = (params) => ({
    type: DELETE_TRANSFORM_OPERATION,
    api: DELETE_TRANSFORMATION_OPERATION,
    params
});

export const doOpenOperationsMenu = ({ attachTo, inputNodeId, outputNodeIds, appendToStack }) => ({
    type: OPEN_OPERATIONS_MENU,
    payload: { attachTo, inputNodeId, outputNodeIds, appendToStack }
});

export const doCloseOperationsMenu = () => ({
    type: CLOSE_OPERATIONS_MENU
});
